<template>
  <div class="min-h-screen bg-gray-900">
    <!-- Header -->
    <div class="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-4xl font-bold mb-4">{{ $t('terms.title') }}</h1>
        <p class="text-xl text-gray-300">{{ $t('terms.subtitle') }}</p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div class="prose prose-lg max-w-none">
        <!-- Last Updated -->
        <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
          <p class="text-blue-700">
            <strong>{{ $t('terms.lastUpdated') }}:</strong> {{ buildDate }}
          </p>
        </div>

        <!-- Introduction -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.introduction.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.introduction.content') }}</p>
          <p class="text-gray-300">{{ $t('terms.introduction.acceptance') }}</p>
        </section>

        <!-- Company Information -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.company.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('terms.company.name') }}:</strong> Heidelbell</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('terms.company.address') }}:</strong> {{ $t('contact.info.address.value') }}</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('terms.company.email') }}:</strong> connect@heidelbell.com</p>
            <p class="text-gray-300"><strong>{{ $t('terms.company.phone') }}:</strong> {{ $t('contact.info.phoneValue') }}</p>
          </div>
        </section>

        <!-- Services -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.services.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.services.intro') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.services.offered.title') }}</h3>
          <ul class="list-disc pl-6 mb-6 text-gray-300">
            <li>{{ $t('terms.services.offered.infrastructure') }}</li>
            <li>{{ $t('terms.services.offered.helpdesk') }}</li>
            <li>{{ $t('terms.services.offered.project') }}</li>
            <li>{{ $t('terms.services.offered.development') }}</li>
            <li>{{ $t('terms.services.offered.optimization') }}</li>
            <li>{{ $t('terms.services.offered.data') }}</li>
          </ul>

          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.services.quality.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.services.quality.content') }}</p>
        </section>

        <!-- Client Obligations -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.client.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.client.intro') }}</p>
          
          <ul class="list-disc pl-6 text-gray-300">
            <li>{{ $t('terms.client.cooperation') }}</li>
            <li>{{ $t('terms.client.information') }}</li>
            <li>{{ $t('terms.client.access') }}</li>
            <li>{{ $t('terms.client.payment') }}</li>
            <li>{{ $t('terms.client.confidentiality') }}</li>
          </ul>
        </section>

        <!-- Payment Terms -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.payment.title') }}</h2>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.payment.invoicing.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.payment.invoicing.content') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.payment.terms.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.payment.terms.content') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.payment.late.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.payment.late.content') }}</p>
        </section>

        <!-- Intellectual Property -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.intellectual.title') }}</h2>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.intellectual.heidelbell.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.intellectual.heidelbell.content') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.intellectual.client.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.intellectual.client.content') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.intellectual.third.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.intellectual.third.content') }}</p>
        </section>

        <!-- Confidentiality -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.confidentiality.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.confidentiality.intro') }}</p>
          
          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <h4 class="font-semibold text-yellow-800 mb-2">{{ $t('terms.confidentiality.obligation.title') }}</h4>
            <p class="text-yellow-700">{{ $t('terms.confidentiality.obligation.content') }}</p>
          </div>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.confidentiality.exceptions.title') }}</h3>
          <ul class="list-disc pl-6 text-gray-300">
            <li>{{ $t('terms.confidentiality.exceptions.public') }}</li>
            <li>{{ $t('terms.confidentiality.exceptions.legal') }}</li>
            <li>{{ $t('terms.confidentiality.exceptions.consent') }}</li>
          </ul>
        </section>

        <!-- Liability -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.liability.title') }}</h2>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.liability.limitation.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.liability.limitation.content') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.liability.exclusions.title') }}</h3>
          <ul class="list-disc pl-6 text-gray-300">
            <li>{{ $t('terms.liability.exclusions.indirect') }}</li>
            <li>{{ $t('terms.liability.exclusions.consequential') }}</li>
            <li>{{ $t('terms.liability.exclusions.third') }}</li>
          </ul>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.liability.maximum.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.liability.maximum.content') }}</p>
        </section>

        <!-- Termination -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.termination.title') }}</h2>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.termination.notice.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.termination.notice.content') }}</p>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.termination.immediate.title') }}</h3>
          <ul class="list-disc pl-6 text-gray-300">
            <li>{{ $t('terms.termination.immediate.breach') }}</li>
            <li>{{ $t('terms.termination.immediate.insolvency') }}</li>
            <li>{{ $t('terms.termination.immediate.force') }}</li>
          </ul>
          
          <h3 class="text-xl font-semibold text-white mb-3">{{ $t('terms.termination.effects.title') }}</h3>
          <p class="text-gray-300 mb-4">{{ $t('terms.termination.effects.content') }}</p>
        </section>

        <!-- Force Majeure -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.forceMajeure.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.forceMajeure.content') }}</p>
        </section>

        <!-- Governing Law -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.governing.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.governing.content') }}</p>
        </section>

        <!-- Dispute Resolution -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.disputes.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.disputes.content') }}</p>
        </section>

        <!-- Severability -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.severability.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.severability.content') }}</p>
        </section>

        <!-- Contact Information -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('terms.contact.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('terms.contact.intro') }}</p>
          
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('terms.contact.email') }}:</strong> connect@heidelbell.com</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('terms.contact.phone') }}:</strong> +49 176 47050093</p>
            <p class="text-gray-300"><strong>{{ $t('terms.contact.address') }}:</strong> {{ $t('contact.info.address.value') }}</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useBuildDate } from '../composables/useBuildDate'

const { t } = useI18n()
const { buildDate } = useBuildDate()

// SEO Meta tags
document.title = t('terms.seo.title')
document.querySelector('meta[name="description"]')?.setAttribute('content', t('terms.seo.description'))
</script>

<style scoped>
.prose {
  @apply text-gray-300 leading-relaxed text-justify;
}

.prose h2 {
  @apply text-2xl font-bold text-white mb-4 mt-8 text-left;
}

.prose h3 {
  @apply text-xl font-semibold text-white mb-3 mt-6 text-left;
}

.prose h4 {
  @apply text-lg font-semibold text-white mb-2 mt-4 text-left;
}

.prose .bg-yellow-50 h4 {
  @apply text-yellow-800;
}

.prose p {
  @apply mb-4 text-justify;
}

.prose ul {
  @apply mb-6;
}

.prose li {
  @apply mb-2;
}
</style> 